import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { trackEvent } from "@/lib/umami";

// Sections of the home page; linked as "/#hash" so they also work from other pages.
const LINKS = [
  { hash: "how", key: "howItWorks" },
  { hash: "extension", key: "extension" },
  { hash: "protection", key: "protection" },
  { hash: "faq", key: "faq" },
] as const;

/**
 * Landing navbar. Transparent with white text over the navy hero, turns into a
 * solid navy bar once the page scrolls so it stays legible over light sections.
 */
export function Navbar() {
  const { t } = useTranslation("landing");
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-200",
        scrolled || open
          ? "border-b border-white/10 bg-[#0a1a33]/95 backdrop-blur"
          : "bg-transparent",
      )}
    >
      <nav className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-5 md:px-10">
        <Link to="/" aria-label="Mailfinity home">
          <Logo tone="light" size={26} />
        </Link>

        <div className="hidden items-center gap-7 text-[15px] font-medium text-[#d5deea] md:flex">
          {LINKS.map((link) => (
            <Link
              key={link.key}
              to="/"
              hash={link.hash}
              className="transition-colors hover:text-white"
            >
              {t(`nav.${link.key}`)}
            </Link>
          ))}
          <LanguageSwitcher tone="light" />
          <Link to="/login" className="transition-colors hover:text-white">
            {t("nav.login")}
          </Link>
          <Button asChild size="sm" className="h-9 px-4">
            <Link
              to="/signup"
              onClick={() => trackEvent("signup_cta_click", { location: "navbar" })}
            >
              {t("nav.start")}
            </Link>
          </Button>
        </div>

        <button
          type="button"
          className="inline-flex size-11 items-center justify-center rounded-full text-white md:hidden"
          aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-white/10 px-5 pb-6 md:hidden">
          <div className="flex flex-col py-2 text-[16px] font-medium text-[#d5deea]">
            {LINKS.map((link) => (
              <Link
                key={link.key}
                to="/"
                hash={link.hash}
                className="py-3 hover:text-white"
                onClick={() => setOpen(false)}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
            <Link to="/login" className="py-3 hover:text-white">
              {t("nav.login")}
            </Link>
          </div>
          <div className="flex items-center justify-between gap-3">
            <LanguageSwitcher tone="light" />
            <Button asChild className="flex-1">
              <Link
                to="/signup"
                onClick={() => trackEvent("signup_cta_click", { location: "navbar" })}
              >
                {t("nav.start")}
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
